import { isBasicLand } from './tcgplayer'

/** Tapfiliate referral code - first-click attribution, 7-day cookie.
 *  Renaming the code in the Mana Pool dashboard kills every shipped link. */
export const MANAPOOL_REF = 'commanderbracket'

/**
 * "qty name" lines, newline-separated, for a store that takes a pasted
 * decklist (Mana Pool, Card Kingdom). Basics are excluded like the TCGPlayer
 * bulk URL, and duplicate names (case-insensitive) have their quantities
 * summed under the first spelling seen.
 */
export function nonBasicDeckLines(cards: { name: string; quantity?: number }[]): string {
  const counts = new Map<string, { name: string; qty: number }>()
  for (const c of cards) {
    if (isBasicLand(c.name)) continue
    const key = c.name.toLowerCase().trim()
    const prev = counts.get(key)
    if (prev) prev.qty += c.quantity ?? 1
    else counts.set(key, { name: c.name, qty: c.quantity ?? 1 })
  }
  return [...counts.values()].map(c => `${c.qty} ${c.name}`).join('\n')
}

/** Mana Pool's card search misses on full "Front // Back" DFC names. */
function frontFaceName(name: string): string {
  return name.split(' // ')[0].trim()
}

/**
 * Mana Pool affiliate link for a single card. Search results page - direct
 * card pages need set+collector number the API doesn't send (deferred).
 */
export function manapoolCardUrl(cardName: string): string {
  // eslint-disable-next-line i18next/no-literal-string -- URL
  return `https://manapool.com/cards?q=${encodeURIComponent(frontFaceName(cardName))}&ref=${MANAPOOL_REF}`
}

/**
 * Mana Pool mass-entry link with the decklist pre-filled via the base64
 * `deck` param (CubeCobra's production pattern; undocumented, so if Mana Pool
 * ever drops it the link still lands on the paste-a-list page). Quantities of
 * duplicate names are summed; basics are excluded like the TCGPlayer bulk URL.
 * The base64 is percent-encoded so `+ / =` survive query-string decoding.
 */
export function manapoolBulkBuyUrl(cards: { name: string; quantity?: number }[]): string {
  const decklist = nonBasicDeckLines(cards)
  // UTF-8-safe base64: btoa alone throws on non-latin1 (accented card names).
  const encoded = encodeURIComponent(btoa(unescape(encodeURIComponent(decklist))))
  // eslint-disable-next-line i18next/no-literal-string -- URL
  return `https://manapool.com/add-deck?ref=${MANAPOOL_REF}&deck=${encoded}`
}
